import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'

interface SageCredentials {
  id: string
  business_id: string | null
  expires_at: string
  scope: string | null
  created_at: string
}

const SAGE_OAUTH_BASE = 'https://www.sageone.com/oauth2'
const SAGE_CALLBACK_URL = 'https://rvpqestlzyidwtoturfe.supabase.co/functions/v1/sage-oauth-callback'

export default function AdminSage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [credentials, setCredentials] = useState<SageCredentials | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchCredentials = async () => {
    setLoading(true)
    // RLS restricts this to ADMIN (see sage_credentials migration) -- access/refresh tokens
    // themselves are never selected here, only the connection metadata needed to show status.
    const { data } = await supabase
      .from('sage_credentials')
      .select('id, business_id, expires_at, scope, created_at')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    setCredentials(data)
    setLoading(false)
  }

  useEffect(() => { fetchCredentials() }, [])

  useEffect(() => {
    if (searchParams.get('connected') || searchParams.get('error')) {
      fetchCredentials()
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const connectedMsg = searchParams.get('connected')
  const errorMsg = searchParams.get('error')

  const handleConnect = () => {
    const clientId = import.meta.env.VITE_SAGE_CLIENT_ID
    if (!clientId) {
      alert('VITE_SAGE_CLIENT_ID is not configured in this build -- see Admin > Connect Sage setup notes.')
      return
    }
    const url = new URL(`${SAGE_OAUTH_BASE}/auth/central`)
    url.searchParams.set('client_id', clientId)
    url.searchParams.set('redirect_uri', SAGE_CALLBACK_URL)
    url.searchParams.set('scope', 'full_access')
    url.searchParams.set('response_type', 'code')
    if (user?.id) url.searchParams.set('state', user.id)
    window.location.href = url.toString()
  }

  const expired = credentials ? new Date(credentials.expires_at).getTime() < Date.now() : false

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Connect Sage</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sage Business Cloud Accounting -- syncs customer contacts and sales invoices.
        </p>
      </div>

      {connectedMsg && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm">
          Successfully connected to Sage.
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
          {decodeURIComponent(errorMsg)}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading connection status…</p>
        ) : credentials ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                expired ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {expired ? 'Token expired — will auto-refresh on next sync' : 'Connected'}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Business: {credentials.business_id || '— (not returned by Sage on connect)'}
            </p>
            <p className="text-xs text-gray-400">
              Connected {new Date(credentials.created_at).toLocaleString('en-GB')} · Scope: {credentials.scope || '—'}
            </p>
            <button
              onClick={handleConnect}
              className="mt-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium"
            >
              Reconnect Sage
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">Not connected to Sage yet.</p>
            <button
              onClick={handleConnect}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Connect Sage
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
